import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Sparkles, ArrowLeft, LayoutGrid, GalleryHorizontal } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BUCKETS, BUCKET_MAP, BUCKET_ACCENTS } from "../data/buckets";
import { fetchMedia, resolveUrl } from "../lib/api";
import { CoverflowCarousel } from "./ui/coverflow-carousel";

export default function MemoryLane() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("categories"); // "categories" | "gallery"
  const [active, setActive] = useState(null);
  const [layout, setLayout] = useState("coverflow"); // "coverflow" | "grid"
  const [flowIdx, setFlowIdx] = useState(0);
  const [catLayout, setCatLayout] = useState("coverflow"); // "coverflow" | "grid"
  const [catIdx, setCatIdx] = useState(0);
  const navigate = useNavigate();

  const openMemory = (m) => {
    if (!m?.id) return;
    navigate(`/memory/${m.id}`);
  };

  useEffect(() => {
    fetchMedia()
      .then((data) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const byBucket = useMemo(() => {
    const map = {};
    BUCKETS.forEach((b) => (map[b.key] = []));
    items.forEach((i) => {
      if (!map[i.bucket]) map[i.bucket] = [];
      map[i.bucket].push(i);
    });
    Object.keys(map).forEach((k) => map[k].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    return map;
  }, [items]);

  const coverByBucket = useMemo(() => {
    const covers = {};
    BUCKETS.forEach((b) => {
      const list = byBucket[b.key] || [];
      if (!list.length) return;
      // Prefer image over video for the cover; pick a "random-ish" one seeded by bucket
      const images = list.filter((x) => x.media_type === "image");
      const pool = images.length ? images : list;
      const idx = Math.abs(b.key.charCodeAt(0) + b.key.charCodeAt(b.key.length - 1) + list.length) % pool.length;
      covers[b.key] = pool[idx];
    });
    return covers;
  }, [byBucket]);

  const activeBucket = active ? BUCKET_MAP[active] : null;
  const galleryItems = active ? byBucket[active] || [] : [];

  const openBucket = (key) => {
    setActive(key);
    setView("gallery");
    setTimeout(() => {
      const el = document.getElementById("memories");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const backToCategories = () => {
    setView("categories");
    setActive(null);
  };

  return (
    <section
      id="memories"
      data-testid="memories-section"
      className="py-24 md:py-32 bg-[#EAF0F6]/40"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <AnimatePresence mode="wait">
          {view === "categories" ? (
            <motion.div
              key="cats"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
            >
              <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
                <div>
                  <p className="font-body text-xs uppercase tracking-[0.35em] text-[#7B9ACC]">
                    Chapter two
                  </p>
                  <h2 className="mt-3 font-display font-bold text-4xl sm:text-5xl text-[#0B192C] tracking-tight">
                    Memory Lane, by the <br className="hidden sm:block" />
                    mood you're in.
                  </h2>
                </div>
                <p className="max-w-md font-body text-[#334E68] text-base leading-relaxed">
                  Pick a chapter. Each one opens like a little story — with a
                  cover photo, an intro, and every memory tucked inside.
                </p>
              </div>

              {loading ? (
                <div className="py-16 text-center font-body text-[#334E68]">Loading memories…</div>
              ) : (() => {
                const activeBuckets = BUCKETS.filter((b) => (byBucket[b.key] || []).length > 0);
                if (!activeBuckets.length) {
                  return <div className="py-16 text-center font-body text-[#334E68]">No memories uploaded yet.</div>;
                }
                const centerBucket = activeBuckets[Math.min(catIdx, activeBuckets.length - 1)];
                const centerCount = (byBucket[centerBucket.key] || []).length;

                return (
                  <>
                    <div className="flex items-center justify-end mb-4">
                      <div className="flex items-center gap-1 p-1 rounded-full bg-white border border-[#EAF0F6]" data-testid="category-layout-toggle">
                        <button
                          onClick={() => setCatLayout("coverflow")}
                          data-testid="category-layout-coverflow"
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-body transition-colors ${
                            catLayout === "coverflow" ? "bg-[#0B192C] text-white" : "text-[#334E68] hover:bg-[#EAF0F6]"
                          }`}
                        >
                          <GalleryHorizontal className="w-3.5 h-3.5" /> Coverflow
                        </button>
                        <button
                          onClick={() => setCatLayout("grid")}
                          data-testid="category-layout-grid"
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-body transition-colors ${
                            catLayout === "grid" ? "bg-[#0B192C] text-white" : "text-[#334E68] hover:bg-[#EAF0F6]"
                          }`}
                        >
                          <LayoutGrid className="w-3.5 h-3.5" /> Grid
                        </button>
                      </div>
                    </div>

                    {catLayout === "coverflow" ? (
                      <div data-testid="category-coverflow" className="relative -mx-5 sm:-mx-8">
                        <CoverflowCarousel
                          slides={activeBuckets.map((b) => {
                            const cover = coverByBucket[b.key];
                            const isVid = cover?.media_type === "video";
                            return {
                              src: cover ? resolveUrl(cover.url) : "",
                              poster: cover?.poster_url ? resolveUrl(cover.poster_url) : (isVid ? "" : (cover ? resolveUrl(cover.url) : "")),
                              type: isVid ? "video" : "image",
                              alt: b.label,
                              title: b.label,
                              subtitle: b.micro,
                            };
                          })}
                          cardWidth="clamp(220px, 30vw, 360px)"
                          rotate={40}
                          depth={0.55}
                          perspective={2.8}
                          loop={activeBuckets.length > 3}
                          showNavigation
                          cinemaEnabled
                          cinemaDefaultOn
                          onIndexChange={setCatIdx}
                          label="Memory categories coverflow"
                          cardClassName="ring-1 ring-white/40"
                        />
                        <div className="max-w-2xl mx-auto text-center mt-2 px-5">
                          <AnimatePresence mode="wait">
                            <motion.div
                              key={centerBucket.key}
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -6 }}
                              transition={{ duration: 0.35 }}
                            >
                              <p className="font-body text-[10px] uppercase tracking-[0.35em] text-[#7B9ACC]">
                                {centerCount} {centerCount === 1 ? "memory" : "memories"}
                              </p>
                              <h3 className="mt-2 font-display italic text-3xl sm:text-4xl text-[#0B192C] leading-tight">
                                {centerBucket.label}
                              </h3>
                              <p className="mt-2 font-body text-sm sm:text-base text-[#334E68] max-w-xl mx-auto">
                                {centerBucket.micro}
                              </p>
                            </motion.div>
                          </AnimatePresence>
                          <button
                            onClick={() => openBucket(centerBucket.key)}
                            data-testid="category-coverflow-open"
                            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#0B192C] hover:bg-[#050914] text-white text-sm font-body px-5 py-2.5 transition-colors"
                          >
                            Open the chapter <Sparkles className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        data-testid="category-grid"
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
                      >
                        {activeBuckets.map((b, i) => (
                          <CategoryCard
                            key={b.key}
                            bucket={b}
                            cover={coverByBucket[b.key]}
                            count={(byBucket[b.key] || []).length}
                            delay={Math.min(i * 0.04, 0.4)}
                            onClick={() => openBucket(b.key)}
                          />
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </motion.div>
          ) : (
            <motion.div
              key="gallery"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
            >
              <button
                data-testid="back-to-categories"
                onClick={backToCategories}
                className="group inline-flex items-center gap-2 text-sm font-body text-[#334E68] hover:text-[#0B192C] mb-6"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                All categories
              </button>

              <div className="mb-6 max-w-3xl flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="font-body text-xs uppercase tracking-[0.35em] text-[#7B9ACC]">
                    {galleryItems.length} memor{galleryItems.length === 1 ? "y" : "ies"}
                  </p>
                  <h2 className="mt-3 font-display font-bold text-4xl sm:text-5xl text-[#0B192C] tracking-tight">
                    {activeBucket?.label}
                  </h2>
                  <p className="mt-4 font-body text-[#334E68] leading-relaxed text-base md:text-lg">
                    {activeBucket?.intro}
                  </p>
                </div>
                <div className="flex items-center gap-1 p-1 rounded-full bg-white border border-[#EAF0F6]" data-testid="memory-layout-toggle">
                  <button
                    onClick={() => setLayout("coverflow")}
                    aria-pressed={layout === "coverflow"}
                    data-testid="memory-layout-coverflow"
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-body transition-colors ${
                      layout === "coverflow" ? "bg-[#0B192C] text-white" : "text-[#334E68] hover:bg-[#EAF0F6]"
                    }`}
                  >
                    <GalleryHorizontal className="w-3.5 h-3.5" /> Coverflow
                  </button>
                  <button
                    onClick={() => setLayout("grid")}
                    aria-pressed={layout === "grid"}
                    data-testid="memory-layout-grid"
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-body transition-colors ${
                      layout === "grid" ? "bg-[#0B192C] text-white" : "text-[#334E68] hover:bg-[#EAF0F6]"
                    }`}
                  >
                    <LayoutGrid className="w-3.5 h-3.5" /> Grid
                  </button>
                </div>
              </div>

              {layout === "coverflow" && galleryItems.length > 0 ? (
                <div data-testid="memory-coverflow" className="relative -mx-5 sm:-mx-8">
                  <CoverflowCarousel
                    slides={galleryItems.map((m) => {
                      const isVid = m.media_type === "video";
                      return {
                        src: resolveUrl(m.url),
                        poster: m.poster_url ? resolveUrl(m.poster_url) : (isVid ? "" : resolveUrl(m.url)),
                        type: isVid ? "video" : "image",
                        alt: m.title || "memory",
                        title: m.title || "",
                        subtitle: m.caption || "",
                      };
                    })}
                    cardWidth="clamp(200px, 32vw, 380px)"
                    rotate={42}
                    depth={0.55}
                    perspective={2.8}
                    loop={galleryItems.length > 3}
                    showNavigation
                    cinemaEnabled
                    cinemaDefaultOn
                    onIndexChange={setFlowIdx}
                    label={`${activeBucket?.label} coverflow`}
                    cardClassName="ring-1 ring-white/40"
                  />
                  {/* Active-slide caption + open */}
                  <div className="max-w-2xl mx-auto text-center mt-2 px-5">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={flowIdx}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.35 }}
                      >
                        {galleryItems[flowIdx]?.title && (
                          <h3 className="font-display italic text-2xl sm:text-3xl text-[#0B192C]">
                            {galleryItems[flowIdx].title}
                          </h3>
                        )}
                        {galleryItems[flowIdx]?.caption && (
                          <p className="mt-2 font-body text-sm sm:text-base text-[#334E68] max-w-xl mx-auto">
                            {galleryItems[flowIdx].caption}
                          </p>
                        )}
                      </motion.div>
                    </AnimatePresence>
                    <button
                      onClick={() => openMemory(galleryItems[flowIdx])}
                      data-testid="memory-coverflow-open"
                      className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#0B192C] hover:bg-[#050914] text-white text-sm font-body px-5 py-2.5 transition-colors"
                    >
                      Open full memory <Sparkles className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  data-testid="memory-grid"
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
                >
                  {galleryItems.map((m, i) => (
                    <MemoryCard
                      key={m.id}
                      item={m}
                      delay={Math.min(i * 0.03, 0.3)}
                      onOpen={() => openMemory(m)}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

function CategoryCard({ bucket, cover, count, delay, onClick }) {
  const accent = BUCKET_ACCENTS[bucket.key] || "#A7C7E7";
  return (
    <motion.button
      layout
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      onClick={onClick}
      data-testid={`category-card-${bucket.key}`}
      className="group text-left rounded-3xl overflow-hidden bg-white border border-[#EAF0F6] card-shadow card-shadow-hover"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[#EAF0F6]">
        {cover ? (
          cover.media_type === "video" ? (
            <video src={resolveUrl(cover.url)} muted playsInline preload="metadata" className="w-full h-full object-cover" />
          ) : (
            <img
              src={resolveUrl(cover.url)}
              alt={bucket.label}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#7B9ACC]">
            <Sparkles className="w-8 h-8" />
          </div>
        )}
        {/* Bottom gradient */}
        <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-[#0B192C]/95 via-[#0B192C]/50 to-transparent" />

        {/* Count pill */}
        <span
          className="absolute top-3 right-3 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/90 text-[#0B192C] font-body"
          style={{ boxShadow: `0 0 0 2px ${accent}33` }}
        >
          {count} {count === 1 ? "memory" : "memories"}
        </span>

        {/* Content overlaid */}
        <div className="absolute inset-x-0 bottom-0 p-5 text-[#F7F9FC]">
          <h3 className="font-display font-bold text-2xl tracking-tight leading-tight">
            {bucket.label}
          </h3>
          <p className="mt-1.5 font-body text-xs text-[#D6E4F0] line-clamp-2">
            {bucket.micro}
          </p>
          <div className="mt-3 flex items-center gap-1.5 text-xs font-body text-[#A7C7E7] opacity-90 group-hover:opacity-100 transition-opacity">
            <span>Open the chapter</span>
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
          </div>
        </div>
      </div>
    </motion.button>
  );
}

function MemoryCard({ item, delay, onOpen }) {
  const accent = BUCKET_ACCENTS[item.bucket] || "#A7C7E7";
  return (
    <motion.button
      layout
      initial={{ opacity: 0, y: 22 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.5, delay }}
      onClick={onOpen}
      data-testid={`memory-card-${item.id}`}
      className="group text-left rounded-3xl overflow-hidden bg-white border border-[#EAF0F6] card-shadow card-shadow-hover hover:-translate-y-1 transition-transform duration-300"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[#EAF0F6]">
        {item.media_type === "video" ? (
          <>
            <video src={resolveUrl(item.url)} muted playsInline preload="metadata" className="w-full h-full object-cover" />
            <div className="absolute inset-0 flex items-center justify-center bg-black/25 opacity-90 group-hover:opacity-100 transition-opacity">
              <span className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                <Play className="w-5 h-5 text-[#0B192C]" fill="#0B192C" />
              </span>
            </div>
          </>
        ) : (
          <img
            src={resolveUrl(item.url)}
            alt={item.title || "memory"}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
          />
        )}
        {item.media_type === "image" && (
          <Sparkles className="absolute top-3 right-3 w-4 h-4 text-white/85 drop-shadow" />
        )}
      </div>
      <div className="p-5">
        {item.title && (
          <h4 className="font-display font-bold text-lg text-[#0B192C] tracking-tight">
            {item.title}
          </h4>
        )}
        {item.caption && (
          <p className="mt-2 font-body text-sm text-[#334E68] line-clamp-3">
            {item.caption}
          </p>
        )}
      </div>
    </motion.button>
  );
}
